// The fetch side of the calendar. This file owns every network request
// for a month of calendar data and every decision about whether to make
// one: the in-memory LRU (./month_cache), the IndexedDB copies, the
// dedupe of in-flight requests, the freshness window, and the
// navigation guard that lets the newest navigation win.
//
// The calendar's data path is four files, one job each:
//   stores/month_fetch.js          fetch, cache lookups, freshness  (this file)
//   stores/month_cache.js          the in-memory LRU
//   stores/data_store_calendar.js  render: turn a month into calendar events,
//                                  subscribe to live updates
//   helpers/pusher_client.js       the live-update transport, and the
//                                  end-to-end protocol in its header
//
// The DataStore only renders — it hands loadForNavigation/revalidate a
// `render` callback (its loadMonth action) and this module decides
// which data reaches it, if any.
//
// This state is module-level on purpose: the boot-time prefetch
// (index.jsx) runs before the calendar asks for any month, and the
// store's navigation must adopt that same in-flight request instead of
// racing it.
import axios from "axios";
import Cookie from "js-cookie";
import dayjs from "dayjs";
import { set as kvSet, del as kvDel, clear as kvClear } from "idb-keyval";

import * as monthCache from "./month_cache";
import createVersionGuard from "../helpers/version_guard";
import handleAxiosError from "../helpers/handle_axios_error";
import { toCommunityDayjs } from "../helpers/helpers";
import { communityApiPath } from "../helpers/session";
import { readDeviceCopy } from "../helpers/device_copy";

// Stale-response guard for month navigation. Bumped at the start of
// every navigation and every revalidation; each async continuation
// captures the token at start and checks it before touching the
// screen, so the newest navigation always wins. A superseded fetch
// response is dropped entirely — caching it could overwrite fresher
// data for the same month. A superseded IndexedDB read may still warm
// the in-memory cache (its data is correct under its own key) but
// skips rendering and skips its revalidation fetch.
const navigations = createVersionGuard();

// Network fetches started by a prefetch, keyed like monthCache. When
// the calendar mounts while the boot-time prefetch of the same month
// is still in flight, revalidate adopts the pending request instead
// of racing it with a duplicate one.
const networkInFlight = {};

// Who hears the community's time zone from each month the server
// sends: the DataStore's adoptCommunityTimezone (data_store_app.js
// sets it when the store is made). Only an answer from the server is
// passed on, never a copy of a month from memory or from IndexedDB. A
// copy saved before an admin changed the zone still holds the old one.
// A tab that was closed or offline at the change keeps its copies (only
// tabs that were connected then clear them), and showing one of them
// would move "today" and every time back to the old zone until the
// server's answer came, or for good if that fetch failed.
var zoneListener = null;

export function onServerZone(listener) {
  zoneListener = listener;
}

function tellZone(answer) {
  if (zoneListener !== null) zoneListener(answer.timezone);
}

// How long a network-fetched month counts as fresh. Within this
// window loadForNavigation renders it without a revalidation fetch —
// the data just came from the server, so refetching it would repeat
// the same request. Past the window, normal stale-while-revalidate.
const MONTH_FRESH_MS = 5000;

// The copies on disk only make a load faster, and IndexedDB can fail: a
// tab that stays open for weeks (the shared screen) can lose its
// connection. So a failed disk call is logged, and the load goes on as
// if the copy were not there (#111).
function logDiskError(error) {
  console.error("IndexedDB failed; going on without the copy on disk:", error);
}

// A copy that cannot be read counts as a miss (the log returns
// undefined): the caller fetches, and the fetch writes a fresh copy. So
// does a read that does not answer in time (readDeviceCopy, #132).
function readFromDisk(key) {
  return readDeviceCopy(key).catch(logDiskError);
}

function writeToDisk(key, value) {
  return kvSet(key, value).catch(logDiskError);
}

function keyForDate(date) {
  var d = dayjs.utc(date);
  return monthCache.keyFor(
    Cookie.get("community_id"),
    d.format("YYYY"),
    d.format("M"),
  );
}

// The copy of `date`'s month in memory, or undefined. The store draws
// the month on screen again from it when the zone changes.
export function copyInMemory(date) {
  return monthCache.get(keyForDate(date));
}

// Evict one month from both caches and bump its invalidation version,
// so an in-flight read of the old data is discarded.
export function invalidateMonth(communityId, year, month) {
  var key = monthCache.keyFor(communityId, year, month);
  monthCache.remove(key);
  kvDel(key).catch(logDiskError);
  monthCache.bumpVersion(key);
}

// Drop every cached month and meal, in RAM and in IndexedDB. The
// residents channel fires this: a renamed, retired or moved resident
// (or a renamed unit) appears on chips in any month and in every
// meal's sign-up list, and there is no way to know which copies hold
// the old value. IndexedDB holds only these copies, so it is cleared
// whole. The month on screen is refetched by the caller.
export function invalidateAllMonths() {
  monthCache.clear();
  kvClear().catch(logDiskError);
}

// The month on screen is stale (a Pusher update, a reconnect, the day
// changing): drop the copies, so a navigation away and back cannot
// show them, and mark the version, so a prefetch of this month that
// is still on the wire drops its answer instead of storing it —
// revalidate would otherwise adopt that in-flight request and render
// data read before the change. Then fetch.
export function refetch(date, render) {
  var d = dayjs.utc(date);
  invalidateMonth(Cookie.get("community_id"), d.format("YYYY"), d.format("M"));
  revalidate(date, render);
}

// The client that knows, invalidates (issue #37): only the current
// month and its neighbors have Pusher channels, so a reservation or
// event saved onto a farther month would leave that month's cache
// stale — and the person who just made the booking would see it
// missing. The modal that made the change calls this with the
// affected date(s); an edit that moves a date calls it for both the
// old and the new month. Accepts what the modals hold: a picker's day
// ("YYYY-MM-DD", the community's day) or a wire date string (offset or
// naive), which resolves to the community month — the same month the
// cache key uses.
export function invalidateMonthForDate(date) {
  if (!date) return;
  var d = toCommunityDayjs(date);
  // A date we cannot read names no month to evict.
  if (!d.isValid()) return;
  invalidateMonth(Cookie.get("community_id"), d.format("YYYY"), d.format("M"));
}

// Warm the caches for a month nobody is looking at yet: the boot-time
// prefetch (index.jsx calls this before React mounts) and the
// adjacent months of the one on screen. Renders nothing.
export function prefetchMonth(date) {
  var key = keyForDate(date);

  // The read also marks the month as recently used, so an adjacent
  // month that is already cached stays away from the eviction end.
  if (monthCache.get(key) !== undefined) return;

  var versionAtStart = monthCache.versionFor(key);

  readFromDisk(key).then(function (value) {
    // Discard if a Pusher invalidation arrived since we started
    if (monthCache.versionFor(key) !== versionAtStart) return;

    if (value !== null && typeof value !== "undefined") {
      monthCache.set(key, value);
      return;
    }

    // The session can end while the copy on disk is read (#153).
    var path = communityApiPath(`calendar/${date}`);
    if (path === null) return;

    var pending = axios
      .get(path)
      .then(function (response) {
        // Discard if a Pusher invalidation arrived since we started
        if (monthCache.versionFor(key) !== versionAtStart) return;
        tellZone(response.data);
        monthCache.set(key, response.data);
        monthCache.markFresh(key);
        writeToDisk(key, response.data);
      })
      .catch(function (error) {
        // A failed prefetch is not shown to anyone. The error goes to
        // revalidate, if the calendar adopted this request, so it can
        // tell a request that got no answer from one that got an error.
        return error;
      })
      .finally(function () {
        delete networkInFlight[key];
      });
    networkInFlight[key] = pending;
  });
}

// A navigation to `date`: render the best copy we have right away,
// then revalidate against the server unless the copy is seconds old.
export function loadForNavigation(date, render) {
  var token = navigations.bump();
  var key = keyForDate(date);

  // Synchronous in-memory cache: instant render, no blank flash
  var cached = monthCache.get(key);
  if (cached !== undefined) {
    render(cached);
    // Data that came from the network seconds ago (a boot-time or
    // adjacent-month prefetch, or a quick back-and-forth) needs no
    // revalidation — the fetch would repeat the same request.
    // Older entries revalidate as always.
    if (!monthCache.isFresh(key, MONTH_FRESH_MS)) {
      revalidate(date, render);
    }
    return;
  }

  // Async IndexedDB fallback
  var versionAtStart = monthCache.versionFor(key);
  readFromDisk(key).then(function (value) {
    if (value === null || typeof value === "undefined") {
      // User already navigated elsewhere: nothing to fetch here.
      if (!navigations.isCurrent(token)) return;
      revalidate(date, render);
    } else {
      // Warming the in-memory cache is safe even when superseded —
      // the data sits under its own key. Unless a push dropped this
      // month during the read: the copy was saved before the push,
      // and kept in memory it would be drawn on the next visit.
      if (monthCache.versionFor(key) === versionAtStart) {
        monthCache.set(key, value);
      }
      // User already navigated elsewhere: skip the render and the
      // revalidation fetch.
      if (!navigations.isCurrent(token)) return;
      render(value);
      revalidate(date, render);
    }
  });
}

// How long revalidate waits before it asks again for a month whose
// adopted prefetch got no answer (#163). A page load stops the requests
// the page has on the wire, and the prefetch then fails with no answer.
// The page is leaving, so a new request is useless, and WebKit refuses
// it and logs "... due to access control checks." So the request is
// sent only after this wait. Once the page is gone, its timers never
// run, so nothing is sent.
//
// The old page stays alive until the new page's answer arrives. If
// that takes longer than this wait, the request is still sent while
// the page is leaving. Then it is refused and logged, as before this
// wait existed, and nothing changes on screen; the new page asks for
// its own month anyway. So the wait is longer than a page load takes
// on a working connection. It is also how long a person on a page that
// stays waits for the month after the network fails once.
export const NO_ANSWER_RETRY_MS = 3000;

// The request got no answer: the network failed, or the browser stopped
// it because the page is leaving. WebKit reports both as ERR_NETWORK.
// The test is "no response" and not that code, so a request that ended
// as an abort is treated the same way. An answer of any status means
// the request was not stopped, so the page is not leaving.
function gotNoAnswer(error) {
  return error !== undefined && error.response === undefined;
}

// Fetch `date` from the server and render it — unless a prefetch of
// the same month is still on the wire (the boot-time prefetch,
// usually): then wait for it and render its result instead of issuing
// a duplicate request. If it got no answer, ask again after
// NO_ANSWER_RETRY_MS. If it was answered with an error, or a push
// dropped its answer while it was on the wire, fetch again at once.
export function revalidate(date, render) {
  var token = navigations.bump();
  var key = keyForDate(date);
  var pending = networkInFlight[key];
  if (pending) {
    pending.then(function (failure) {
      if (!navigations.isCurrent(token)) return;
      var cached = monthCache.get(key);
      if (cached !== undefined) {
        render(cached);
      } else if (gotNoAnswer(failure)) {
        setTimeout(function () {
          // A navigation or a refetch since then asked for what it
          // needs itself.
          if (!navigations.isCurrent(token)) return;
          fetchMonth(date, token, render);
        }, NO_ANSWER_RETRY_MS);
      } else {
        fetchMonth(date, token, render);
      }
    });
    return;
  }
  fetchMonth(date, token, render);
}

function fetchMonth(date, token, render) {
  // The session can end while the copy on disk is read, or while a
  // calendar that mounted just then asks for its month (#153).
  var path = communityApiPath(`calendar/${date}`);
  if (path === null) return;
  axios
    .get(path)
    .then(function (response) {
      // A newer navigation or refetch superseded this response:
      // drop it entirely. Rendering it would show the wrong month;
      // caching it could overwrite fresher same-month data.
      if (!navigations.isCurrent(token)) return;
      var respData = response.data;
      tellZone(respData);
      var key = monthCache.keyFor(respData.id, respData.year, respData.month);
      monthCache.set(key, respData);
      monthCache.markFresh(key);
      writeToDisk(key, respData).then(function () {
        if (!navigations.isCurrent(token)) return;
        render(respData);
      });
    })
    .catch(function (error) {
      handleAxiosError(error, { silent: true });
    });
}
